// Sample data for template preview
import type {
  BusinessData,
  Recipient,
  LineItemSection,
  InvoiceData,
  QuoteData,
  CreditMemoData,
  PurchaseOrderData,
  DocumentData,
  DocumentType,
} from './types';

// Shared business data (the company sending documents)
export const SAMPLE_BUSINESS: BusinessData = {
  id: 'bus_sample123',
  name: 'Pro Electric Services',
  address: '456 Commerce St\nLos Angeles, CA 90001',
  industry: 'electrical',
  logo: '',
  phone: '(555) 123-4567',
  email: 'info@proelectric.com',
};

// Shared client data (recipient for invoices, quotes, credit memos)
export const SAMPLE_CLIENT: Recipient = {
  id: 'cl_sample456',
  billing_name: 'Acme Corporation',
  company_name: 'Acme Corp',
  address: '123 Business Ave\nSan Francisco, CA 94102',
  emails: ['billing@acme.com'],
  phones: [{ country_code: '+1', number: '555-987-6543' }],
  payment_terms: 'Net 30',
};

// Shared vendor data (recipient for purchase orders)
export const SAMPLE_VENDOR: Recipient = {
  id: 'cl_vendor789',
  billing_name: 'Electrical Supply Co',
  company_name: 'ElecSupply Inc',
  address: '789 Warehouse Blvd\nOakland, CA 94607',
  emails: ['orders@elecsupply.com'],
  phones: [{ country_code: '+1', number: '555-111-2222' }],
};

// Sample line items (shared structure, can be filtered by item_type)
export const SAMPLE_LINE_ITEMS: LineItemSection[] = [
  ['Labor & Services', [
    { name: 'Electrical Installation', code: 'SVC-001', description: 'Panel upgrade installation labor', quantity: 8, rate: 150, item_type: 'labor', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 120, amount: 1200 },
    { name: 'Consultation', code: 'SVC-002', description: 'Site assessment and planning', quantity: 2, rate: 200, item_type: 'labor', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 40, amount: 400 },
  ]],
  ['Parts & Materials', [
    { name: '200A Electrical Panel', code: 'MAT-001', description: 'Square D 200-amp main breaker panel', quantity: 1, rate: 450, item_type: 'product_parts', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 45, amount: 450 },
    { name: 'Copper Wire 6 AWG', code: 'MAT-002', description: '6 AWG copper wire, 100ft roll', quantity: 2, rate: 185, item_type: 'product_parts', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 37, amount: 370 },
    { name: 'Circuit Breakers', code: 'MAT-003', description: '20A single-pole breakers (pack of 10)', quantity: 1, rate: 95, item_type: 'product_parts', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 9.50, amount: 95 },
  ]],
];

// Sample invoice data matching InvoiceSerializer fields
export const SAMPLE_INVOICE: InvoiceData = {
  documentType: 'invoice',
  id: 'inv_sample001',
  invoice_number: 'INV-2025-0042',
  client: SAMPLE_CLIENT,
  client_name: SAMPLE_CLIENT.billing_name,
  client_company: SAMPLE_CLIENT.company_name,
  client_address: SAMPLE_CLIENT.address,
  invoice_date: '2025-12-24',
  due_date: '2026-01-23',
  payment_terms: 'Net 30',
  status: 'awaiting_payment',
  line_items: SAMPLE_LINE_ITEMS,
  deposit: 0,
  stripe_card_enabled: true,
  stripe_ach_enabled: false,
  // Shipping sample data
  ship_enabled: true,
  ship_fee: '45.00',
  ship_date: '2025-12-26',
  ship_via: 'UPS Ground',
  ship_tracking_number: '1Z999AA10123456784',
  requires_my_signature: false,
  requires_client_signature: false,
  note_for_client: 'Thank you for choosing Pro Electric Services!',
  internal_notes: '',
  attachments: [],
  markup_dollars: 0,
  markup_percent: 0,
  discount_dollars: 0,
  discount_percent: 5,
  subtotal: 2515,
  total_tax: 251.50,
  total_markup: 0,
  total_discount: 125.75,
  total_amount: 2685.75,  // Updated to include shipping
  amount_paid: 500,
  balance_due: 2185.75,   // Updated to include shipping
};

// Sample quote data matching QuoteSerializer fields
export const SAMPLE_QUOTE: QuoteData = {
  documentType: 'quote',
  id: 'quo_sample001',
  quote_number: 'QUO-2025-0018',
  client: SAMPLE_CLIENT,
  client_name: SAMPLE_CLIENT.billing_name,
  client_company: SAMPLE_CLIENT.company_name,
  client_address: SAMPLE_CLIENT.address,
  quote_date: '2025-12-20',
  due_date: '2026-01-20',
  status: 'awaiting_response',
  line_items: SAMPLE_LINE_ITEMS,
  deposit: 500,
  stripe_card_enabled: true,
  stripe_ach_enabled: false,
  // Shipping sample data
  ship_enabled: true,
  ship_fee: '35.00',
  ship_date: '2026-01-05',
  ship_via: 'FedEx',
  ship_tracking_number: '',  // Not yet shipped
  requires_my_signature: false,
  requires_client_signature: true,
  note_for_client: 'This quote is valid for 30 days. Deposit required to schedule work.',
  internal_notes: '',
  attachments: [],
  markup_dollars: 0,
  markup_percent: 10,
  discount_dollars: 0,
  discount_percent: 0,
  subtotal: 2515,
  total_tax: 251.50,
  total_markup: 251.50,
  total_discount: 0,
  total_amount: 3053,  // Updated to include shipping
  amount_paid: 0,
  balance_due: 535,    // Updated to include shipping in deposit
  deposit_fully_paid: false,
};

// Sample credit memo data matching CreditMemoSerializer fields
export const SAMPLE_CREDIT_MEMO: CreditMemoData = {
  documentType: 'credit_memo',
  id: 'cmemo_sample001',
  credit_memo_number: 'CM-2025-0005',
  client: SAMPLE_CLIENT,
  client_name: SAMPLE_CLIENT.billing_name,
  client_company: SAMPLE_CLIENT.company_name,
  client_address: SAMPLE_CLIENT.address,
  credit_memo_date: '2025-12-26',
  status: 'awaiting_allocation',
  line_items: [
    ['Credit Items', [
      { name: 'Returned Materials', code: 'RET-001', description: 'Unused circuit breakers returned', quantity: 1, rate: 95, item_type: 'product_parts', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 9.50, amount: 95 },
      { name: 'Service Adjustment', code: 'ADJ-001', description: 'Labor overcharge correction', quantity: 1, rate: 150, item_type: 'labor', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 15, amount: 150 },
    ]],
  ],
  note_for_client: 'Credit memo for returned materials and billing adjustment.',
  internal_notes: '',
  attachments: [],
  subtotal: 245,
  total_tax: 24.50,
  total_amount: 269.50,
  amount_allocated: 0,
  balance_remaining: 269.50,
  fully_allocated: false,
};

// Sample purchase order data matching PurchaseOrderSerializer fields
export const SAMPLE_PURCHASE_ORDER: PurchaseOrderData = {
  documentType: 'purchase_order',
  id: 'po_sample001',
  purchase_order_number: 'PO-2025-0012',
  vendor: SAMPLE_VENDOR,
  vendor_name: SAMPLE_VENDOR.billing_name,
  vendor_company: SAMPLE_VENDOR.company_name,
  vendor_address: SAMPLE_VENDOR.address,
  issued_date: '2025-12-22',
  status: 'sent',
  line_items: [
    ['Order Items', [
      { name: '200A Electrical Panel', code: 'MAT-001', description: 'Square D 200-amp main breaker panel', quantity: 2, rate: 380, item_type: 'product_parts', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 76, amount: 760 },
      { name: 'Copper Wire 6 AWG', code: 'MAT-002', description: '6 AWG copper wire, 100ft roll', quantity: 5, rate: 160, item_type: 'product_parts', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 80, amount: 800 },
      { name: 'Circuit Breakers', code: 'MAT-003', description: '20A single-pole breakers (pack of 10)', quantity: 3, rate: 75, item_type: 'product_parts', discount: 0, discount_type: 'percentage', tax_rate: 10, tax_amount: 22.50, amount: 225 },
    ]],
  ],
  markup_dollars: 0,
  markup_percent: 0,
  // Shipping sample data
  ship_enabled: true,
  ship_fee: '50.00',
  ship_date: '2025-12-28',
  ship_via: 'Freight',
  ship_tracking_number: 'PRO123456789',
  note_for_vendor: 'Please deliver to job site address. Call before delivery.',
  internal_notes: 'For Acme Corp project',
  attachments: [],
  subtotal: 1785,
  total_tax: 178.50,
  total_markup: 0,
  total_amount: 2013.50,  // Updated to include shipping
};

// Map of sample data by document type
export const SAMPLE_DATA: Record<DocumentType, DocumentData> = {
  invoice: SAMPLE_INVOICE,
  quote: SAMPLE_QUOTE,
  credit_memo: SAMPLE_CREDIT_MEMO,
  purchase_order: SAMPLE_PURCHASE_ORDER,
};

// Helper to get sample data by document type
export function getSampleData(documentType: DocumentType): DocumentData {
  return SAMPLE_DATA[documentType];
}
